// Vendor Libs
import React, {useMemo, useState} from 'react';
import PropTypes from 'prop-types';
import {useDropzone} from 'react-dropzone';
import isEmpty from 'lodash.isempty';

// Material UI
import Card from '@material-ui/core/Card';
import CardHeader from '@material-ui/core/CardHeader';
import CardMedia from '@material-ui/core/CardMedia';
import DeleteForeverIcon from '@material-ui/icons/DeleteForever';
import FormControl from '@material-ui/core/FormControl';
import FormHelperText from '@material-ui/core/FormHelperText';
import Grid from '@material-ui/core/Grid';
import IconButton from '@material-ui/core/IconButton';
import InputLabel from '@material-ui/core/InputLabel';
import {makeStyles} from '@material-ui/core/styles';
import NativeSelect from '@material-ui/core/NativeSelect';

const activeStyle = {
  borderColor: '#2196f3',
};

const acceptStyle = {
  borderColor: '#00e676',
};

const baseStyle = {
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: '20px',
  borderWidth: 2,
  borderRadius: 2,
  borderColor: '#eeeeee',
  borderStyle: 'dashed',
  backgroundColor: '#fafafa',
  color: '#666',
  outline: 'none',
  transition: 'border .24s ease-in-out',
};

const rejectStyle = {
  borderColor: '#ff1744',
};

const useStyles = makeStyles(theme => ({
  cardHeader: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  container: {
    display: 'flex',
    flexDirection: 'column',
    minWidth: 500,
    minHeight: 200,
  },
  dropzone: {
    width: '100%',
  },
  formControl: {
    margin: theme.spacing(0, 2, 0, 0.5),
    minWidth: 160,
  },
  imagesContainer: {
    justifyContent: 'space-around',
  },
  imagePicker: {
    margin: theme.spacing(2, 0, 1, 0),
    display: 'flex',
    flexDirection: 'row',
  },
  required: {
    bottom: -2,
    color: 'red',
    left: 10,
    position: 'absolute',
  },
  selectEmpty: {
    marginTop: theme.spacing(1),
  },
  standardMedia: {
    height: 0,
    paddingTop: '75%', // 4:3
  },
  standardCard: {
    minWidth: 130,
    minHeight: 146,
  },
  title: {
    padding: '2px 0px 2px 10px',
    fontSize: 12,
  },
  wideCard: {
    minWidth: 178,
    minHeight: 148,
    position: 'relative',
  },
  wideMedia: {
    height: 0,
    paddingTop: '56.25%', // 16:9
  },
}));

const MultiImagePicker = ({
  firstDisplayName,
  firstImageRequire,
  firstDisplayType,
  firstImageURL,
  secondDisplayName,
  secondImageRequire,
  secondDisplayType,
  secondImageURL,
  thirdDisplayName,
  thirdImageRequire,
  thirdDisplayType,
  thirdImageURL,
  onAdd,
  onDelete,
}) => {
  const classes = useStyles();
  const [type, setType] = useState('');
  const [images, setImages] = useState({
    firstImage: {},
    secondImage: {},
    thirdImage: {},
  });

  const getImageName = x => {
    if (x === firstDisplayType) {
      return 'firstImage';
    } else if (x === secondDisplayType) {
      return 'secondImage';
    }
    return 'thirdImage';
  };

  const getDisplayName = x => {
    if (x === firstDisplayType) {
      return firstDisplayName.toLowerCase();
    } else if (x === secondDisplayType) {
      return secondDisplayName.toLowerCase();
    }
    return thirdDisplayName.toLowerCase();
  };

  const {getRootProps, getInputProps, isDragActive, isDragAccept, isDragReject} = useDropzone({
    accept: 'image/*',
    disabled: !type,
    multiple: false,
    onDrop: acceptedFiles => {
      const imagePicked = getImageName(type);
      setImages({
        ...images,
        [imagePicked]: acceptedFiles.map(file =>
          Object.assign(file, {
            preview: URL.createObjectURL(file),
          })
        ),
      });
      setType('');
      onAdd(`${type}Bytes`, acceptedFiles[0]);
    },
  });

  const style = useMemo(
    () => ({
      ...baseStyle,
      ...(isDragActive ? activeStyle : {}),
      ...(isDragAccept ? acceptStyle : {}),
      ...(isDragReject ? rejectStyle : {}),
    }),
    [isDragAccept, isDragActive, isDragReject]
  );

  const showPreview = (imgURL, imgType) => {
    const imagePicked = getImageName(imgType);
    return images[imagePicked] && images[imagePicked].length ? (
      <CardMedia
        className={imgType === firstDisplayType ? classes.standardMedia : classes.wideMedia}
        image={images[imagePicked] && images[imagePicked].length ? images[imagePicked][0].preview : null}
        title={images[imagePicked][0].name}
      />
    ) : imgURL ? (
      <CardMedia
        className={imgType === firstDisplayType ? classes.standardMedia : classes.wideMedia}
        image={imgURL}
        title={`${imgType} preview`}
      />
    ) : null;
  };

  const handleDelete = (imageURL, imageType) => {
    const imagePicked = getImageName(imageType);
    setImages({...images, [imagePicked]: []});
    onDelete(`${imageType}Bytes`);
    if (imageURL) {
      onDelete(`${imageType}URL`);
    }
  };

  return (
    <div className={classes.container}>
      <div className={classes.imagePicker}>
        <FormControl className={classes.formControl}>
          <InputLabel htmlFor="image-type-native-helper">Image Type</InputLabel>
          <NativeSelect
            inputProps={{
              name: 'image type',
              id: 'image-type-native-helper',
            }}
            value={type}
            onChange={e => setType(e.target.value)}
          >
            <option value=""> </option>
            <option value={firstDisplayType}>{firstDisplayName}</option>
            <option value={secondDisplayType}>{secondDisplayName}</option>
            <option value={thirdDisplayType}>{thirdDisplayName}</option>
          </NativeSelect>
          <FormHelperText>Select image type to upload</FormHelperText>
        </FormControl>
        <section className={classes.dropzone}>
          {/* eslint-disable react/jsx-props-no-spreading */}
          <div {...getRootProps({style})}>
            <input {...getInputProps()} />
            {type ? (
              <p>Drop {getDisplayName(type)} image here, or click to select</p>
            ) : (
              <p style={{color: 'red'}}>&#8592; Please select an image type to upload</p>
            )}
          </div>
        </section>
      </div>
      <div className={classes.imageViewer}>
        <Grid className={classes.imagesContainer} container={true} spacing={1}>
          <Grid item={true} md={4}>
            <Card className={classes.standardCard}>
              <div className={classes.cardHeader}>
                <CardHeader
                  className={classes.title}
                  title={firstDisplayName}
                  titleTypographyProps={{variant: 'body2'}}
                />
                {images.firstImage.length || firstImageURL ? (
                  <IconButton
                    aria-label="delete thumbnail"
                    color="primary"
                    component="span"
                    disabled={isEmpty(images.firstImage) && firstImageRequire}
                    onClick={() => handleDelete(firstImageURL, firstDisplayType)}
                  >
                    <DeleteForeverIcon />
                  </IconButton>
                ) : null}
              </div>
              {showPreview(firstImageURL, firstDisplayType)}
            </Card>
          </Grid>
          <Grid item={true} md={4}>
            <Card className={classes.wideCard}>
              <div className={classes.cardHeader}>
                <CardHeader
                  className={classes.title}
                  title={secondDisplayName}
                  titleTypographyProps={{variant: 'body2'}}
                />
                {images.secondImage.length || secondImageURL ? (
                  <IconButton
                    aria-label="delete thumbnail"
                    color="primary"
                    component="span"
                    disabled={isEmpty(images.secondImage) && secondImageRequire}
                    onClick={() => handleDelete(secondImageURL, secondDisplayType)}
                  >
                    <DeleteForeverIcon />
                  </IconButton>
                ) : null}
              </div>
              {showPreview(secondImageURL, secondDisplayType)}
              {secondImageURL || images.secondImage.length ? null : <p className={classes.required}>* required</p>}
            </Card>
          </Grid>
          <Grid item={true} md={4}>
            <Card className={classes.wideCard}>
              <div className={classes.cardHeader}>
                <CardHeader
                  className={classes.title}
                  title={thirdDisplayName}
                  titleTypographyProps={{variant: 'body2'}}
                />
                {images.thirdImage.length || thirdImageURL ? (
                  <IconButton
                    aria-label="delete thumbnail"
                    color="primary"
                    component="span"
                    disabled={isEmpty(images.thirdImage) && thirdImageRequire}
                    onClick={() => handleDelete(thirdImageURL, thirdDisplayType)}
                  >
                    <DeleteForeverIcon />
                  </IconButton>
                ) : null}
              </div>
              {showPreview(thirdImageURL, thirdDisplayType)}
            </Card>
          </Grid>
        </Grid>
      </div>
    </div>
  );
};

MultiImagePicker.propTypes = {
  firstDisplayName: PropTypes.string,
  firstDisplayType: PropTypes.string,
  firstImageRequire: PropTypes.bool,
  firstImageURL: PropTypes.string,
  onAdd: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  secondDisplayName: PropTypes.string,
  secondDisplayType: PropTypes.string,
  secondImageRequire: PropTypes.bool,
  secondImageURL: PropTypes.string,
  thirdDisplayName: PropTypes.string,
  thirdDisplayType: PropTypes.string,
  thirdImageRequire: PropTypes.bool,
  thirdImageURL: PropTypes.string,
};

export default MultiImagePicker;
